/**
 * ReprocessDialog Component
 *
 * Confirmation dialog before triggering collection reprocessing.
 * Explains the impact and asks for explicit confirmation.
 */

import { useState } from 'react';
import { useI18n } from '@/i18n';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui';

export interface ReprocessDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Collection name for display */
  collectionName: string;
  /** Number of documents that will be affected */
  documentCount: number;
  /** Callback when user confirms reprocessing */
  onConfirm: () => Promise<void>;
}

export function ReprocessDialog({
  open,
  onOpenChange,
  collectionName,
  documentCount,
  onConfirm,
}: ReprocessDialogProps) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            {t('admin.knowledge.reprocess.dialogTitle')}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {t('admin.knowledge.reprocess.dialogDescription', {
                  name: collectionName,
                  count: documentCount,
                })}
              </p>
              <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
                <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                  <li>{t('admin.knowledge.reprocess.impactSearch')}</li>
                  <li>{t('admin.knowledge.reprocess.impactTime')}</li>
                  <li>{t('admin.knowledge.reprocess.impactCost')}</li>
                </ul>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleConfirm();
            }}
            disabled={loading}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            {loading ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {loading
              ? t('admin.knowledge.reprocess.processing')
              : t('admin.knowledge.reprocess.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default ReprocessDialog;
